import React, { useState } from 'react';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { Check, ChevronDownIcon, Flag, Search } from 'lucide-react';

import Toggle from 'components/common/Toggle';
import { EpicSummary } from 'types/project/epic';
import { WorkStatusCategory } from 'types/project/projectDefaults';

const isDone = (epic: EpicSummary) => epic.workStatusCategory === WorkStatusCategory.DONE;

interface EpicPickerDropdownProps {
  epics: EpicSummary[];
  /** The chosen Epic's uuid; null is no Epic. */
  value: string | null;
  onChange: (epic: EpicSummary | null) => void;
  disabled?: boolean;
  className?: string;
  /**
   * Set for a Task or Bug under a Story: it has the Story's Epic (null when the Story has
   * none) rather than one of its own, so that is shown, with the Story it comes from.
   */
  viaStory?: { storyKey: string; epic: { title: string; workItemKey: string } | null };
}

/** A Story, Task or Bug's parent Epic, or none. */
const EpicPickerDropdown: React.FC<EpicPickerDropdownProps> = ({
  epics,
  value,
  onChange,
  disabled = false,
  className = '',
  viaStory,
}) => {
  const selected = epics.find((epic) => epic.epicUuid === value);
  const shown = selected ?? viaStory?.epic;

  return (
    <Popover className="relative block w-full text-left">
      <PopoverButton
        type="button"
        disabled={disabled}
        className={`flex items-center gap-2 text-sm text-neutral hover:bg-primary-subtle transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
      >
        {shown ? (
          <>
            <Flag size={16} className="shrink-0 text-[#818CF8]" />
            <span className="truncate text-left">{shown.title}</span>
            <span className="shrink-0 text-xs text-neutral-medium">{shown.workItemKey}</span>
          </>
        ) : (
          <span className="truncate text-left text-neutral-medium">None</span>
        )}
        {viaStory && !selected && (
          <span className="shrink-0 text-xs text-neutral-medium">via {viaStory.storyKey}</span>
        )}
        <ChevronDownIcon className="ml-auto w-3 h-3 mr-3 shrink-0" />
      </PopoverButton>

      <PopoverPanel
        transition
        anchor="bottom start"
        className="mt-2 z-[9999] w-[var(--button-width)] min-w-[280px] rounded-xl bg-primary-variant border border-primary-subtle shadow-lg p-2 transition duration-100 ease-out focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0"
      >
        {({ close }) => (
          // Mounted only while open, so the search starts empty each time.
          <EpicOptions
            epics={epics}
            value={value}
            onSelect={(epic) => {
              onChange(epic);
              close();
            }}
          />
        )}
      </PopoverPanel>
    </Popover>
  );
};

interface EpicOptionsProps {
  epics: EpicSummary[];
  value: string | null;
  onSelect: (epic: EpicSummary | null) => void;
}

/** The selected option's check, in a fixed slot so titles line up. */
const CheckSlot: React.FC<{ checked: boolean }> = ({ checked }) => (
  <span className="flex w-4 shrink-0 justify-center pt-0.5">
    {checked && <Check size={16} className="text-[#A78BFA]" />}
  </span>
);

const EpicOption: React.FC<{ epic: EpicSummary; checked: boolean; onSelect: () => void }> = ({
  epic,
  checked,
  onSelect,
}) => {
  const percent = epic.totalCount ? Math.round((epic.doneCount / epic.totalCount) * 100) : 0;
  return (
    <button
      type="button"
      aria-pressed={checked}
      onClick={onSelect}
      className="flex w-full items-start gap-3 rounded-md px-3 py-2.5 text-left transition-colors hover:bg-primary-subtle"
    >
      <CheckSlot checked={checked} />
      <span className="flex min-w-0 flex-1 flex-col gap-1.5">
        <span className="flex min-w-0 items-baseline gap-2">
          <span className="truncate text-sm font-medium text-neutral">{epic.title}</span>
          <span className="shrink-0 text-xs text-neutral-medium">{epic.workItemKey}</span>
          {isDone(epic) && (
            <span className="shrink-0 rounded-full bg-[#10B98122] px-2 py-0.5 text-[10px] font-medium text-[#34D399]">
              Done
            </span>
          )}
        </span>
        <span className="flex items-center gap-3">
          <span className="h-1 w-full max-w-[140px] overflow-hidden rounded-full bg-primary-subtle">
            <span className="block h-full rounded-full bg-[#8B5CF6]" style={{ width: `${percent}%` }} />
          </span>
          <span className="shrink-0 text-xs text-neutral-medium">
            {epic.doneCount} of {epic.totalCount} done
          </span>
        </span>
      </span>
    </button>
  );
};

/**
 * The searchable list of Epics, also used on its own in the Set Epic dialog.
 * Done Epics are hidden until asked for; picking one reopens it.
 */
export const EpicOptions: React.FC<EpicOptionsProps> = ({ epics, value, onSelect }) => {
  const [search, setSearch] = useState('');
  const [showDone, setShowDone] = useState(false);
  const term = search.trim().toLowerCase();
  const matches = term
    ? epics.filter(
        (epic) => epic.title.toLowerCase().includes(term) || epic.workItemKey.toLowerCase().includes(term),
      )
    : epics;
  // The current Epic stays listed even when it is Done, so its check is visible.
  const visible = matches.filter((epic) => showDone || !isDone(epic) || epic.epicUuid === value);
  // Open Epics first.
  const sorted = [...visible].sort((a, b) => Number(isDone(a)) - Number(isDone(b)));
  const hasDone = epics.some(isDone);

  return (
    <div className="flex flex-col">
      <div className="mb-1 flex h-10 items-center gap-2 rounded-lg border border-primary-subtle bg-primary-light px-3">
        <Search size={16} className="shrink-0 text-neutral-medium" />
        <input
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search Epics..."
          className="min-w-0 flex-1 bg-transparent text-sm text-neutral outline-none placeholder:text-neutral-medium"
        />
      </div>
      <div className="max-h-72 overflow-y-auto custom-scrollbar">
        {!term && (
          <button
            type="button"
            aria-pressed={value === null}
            onClick={() => onSelect(null)}
            className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left text-sm text-neutral-medium transition-colors hover:bg-primary-subtle"
          >
            <CheckSlot checked={value === null} />
            No Epic
          </button>
        )}
        {sorted.map((epic) => (
          <EpicOption
            key={epic.epicUuid}
            epic={epic}
            checked={epic.epicUuid === value}
            onSelect={() => onSelect(epic)}
          />
        ))}
        {sorted.length === 0 && (
          <p className="px-3 py-2.5 text-sm text-neutral-medium">
            {term
              ? 'No Epics found'
              : epics.length
                ? 'Every Epic is done. Turn on Show done Epics to pick one.'
                : 'No Epics yet. Create one from the Epics tab.'}
          </p>
        )}
      </div>
      {hasDone && (
        <div className="mt-1 border-t border-primary-subtle px-3 pb-1 pt-3">
          <Toggle checked={showDone} onChange={setShowDone} label="Show done Epics" />
        </div>
      )}
    </div>
  );
};

export default EpicPickerDropdown;
